import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Flex, Image, Text, useToast, VStack,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { useBountyData } from '../../../contexts/BountyDataContext';
import { bountyApi } from '../../../services/api/bounties';
import { ff } from '../../../theme';
import { DisplayHeading } from '../../../components/ui';
import BountyBackButton from '../components/BountyBackButton';
import ProposalFormFields from '../components/ProposalFormFields';
import {
  formValuesToSubmitParams, ProposalFieldKey, ProposalFormValues, proposalToFormValues, validateProposalForm,
} from '../proposalFormUtils';
import { myBountiesPath, proposalSubmittedPath } from '../utils';
import StickyBottomCta from '../components/StickyBottomCta';
import { pageBottomPaddingWithStickyCta } from '../theme';
import lockIcon from '../assets/lock.png';

const ProposeBountyPage: React.FC = () => {
  const { influencerData } = useContext(InfluencerDataContext);
  const { proposals, refetchBounties } = useBountyData();
  const [searchParams] = useSearchParams();
  const editProposalId = searchParams.get('edit');
  const editingProposal = useMemo(
    () => proposals.find(p => p.id === editProposalId && p.status === 'pendingReview'),
    [proposals, editProposalId],
  );
  const isEditMode = !!editingProposal;

  const [values, setValues] = useState<ProposalFormValues>(() => ({
    companyName: editProposalId ? '' : (searchParams.get('company') ?? ''),
    website: '',
    instagramHandle: '',
    category: '',
    proposalNote: '',
  }));
  const [errors, setErrors] = useState<Partial<Record<ProposalFieldKey, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!editingProposal) return;
    setValues(proposalToFormValues(editingProposal));
  }, [editingProposal]);

  const handleChange = (key: ProposalFieldKey, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setErrors(prev => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async () => {
    if (!influencerData?.id) return;
    const found = validateProposalForm(values);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSubmitting(true);
    try {
      const params = formValuesToSubmitParams(values);
      let proposalId: string;
      if (isEditMode && editingProposal) {
        await bountyApi.updateProposal(influencerData.id, editingProposal.id, params);
        proposalId = editingProposal.id;
      } else {
        const res = await bountyApi.proposeBounty(influencerData.id, params);
        proposalId = res.proposalId;
      }
      await refetchBounties();
      if (isEditMode) {
        toast({
          title: 'Submission updated',
          description: 'Your changes were saved.',
          status: 'success', duration: 5000, isClosable: true,
        });
        navigate(myBountiesPath, { replace: true });
        return;
      }
      navigate(proposalSubmittedPath(proposalId), { replace: true, state: params });
    } catch (error) {
      toast({
        title: isEditMode ? 'Could not update bounty' : 'Could not submit bounty',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box minH="100svh" bg="white" fontFamily="display" color={ff.ink} lineHeight="normal" px="20px" pt="12px" pb={pageBottomPaddingWithStickyCta} overflowX="hidden">
      <Box ml="-8px">
        <BountyBackButton to={isEditMode ? myBountiesPath : '/bounties'} replace aria-label="Back to bounties" />
      </Box>
      <VStack spacing="18px" align="stretch" pt="8px">
        <Box>
          <DisplayHeading size="34px">{isEditMode ? 'Edit your bounty' : 'Submit a bounty'}</DisplayHeading>
          <Text fontSize="15px" lineHeight="1.4" color="#55555A" mt="8px">
            Know a brand or business that&apos;d be a great fit? Tell us who — we&apos;ll tell you what the intro is worth.
          </Text>
        </Box>

        <ProposalFormFields values={values} errors={errors} onChange={handleChange} />

        <Flex gap="10px" align="flex-start" fontSize="13px" lineHeight="1.4" color="#55555A">
          <Image src={lockIcon} alt="" w="20px" h="20px" objectFit="contain" flex="none" />
          <Text><Text as="strong" color={ff.ink}>Your information is secure</Text> and never shared.</Text>
        </Flex>
      </VStack>

      <StickyBottomCta>
        <Button
          variant="lime"
          w="full"
          h="56px"
          borderRadius="full"
          fontSize="16px"
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          {isEditMode ? 'Update submission' : 'Submit for Review'}
        </Button>
      </StickyBottomCta>
    </Box>
  );
};

export default ProposeBountyPage;
